import { useEffect, useState, type FormEvent } from 'react';
import { ApiError } from '../api/client';
import { confirmPasswordReset, requestPasswordReset, validatePasswordReset } from '../api/auth';

export function ForgotPasswordForm({ initialEmail, onBack }: { initialEmail: string; onBack: () => void }) {
  const [email, setEmail] = useState(initialEmail);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await requestPasswordReset(email.trim());
      setSent(true);
    } catch (err) {
      setError(err instanceof ApiError && err.code === 'RATE_LIMITED'
        ? 'Příliš mnoho žádostí. Zkuste to později.'
        : 'Žádost se nepodařilo odeslat.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="login__card" onSubmit={onSubmit}>
      <img className="login__logo" src="/assets/platform1-logo.png" alt="Platform 1" />
      <h2 className="login__title">Obnovení hesla</h2>
      {sent ? (
        <>
          <p className="login__hint">
            Pokud k zadanému e-mailu existuje účet, poslali jsme odkaz pro nastavení nového hesla. Platí 15 minut. Zkontrolujte i složku spam.
          </p>
          <button className="login__submit" type="button" onClick={onBack}>Zpět na přihlášení</button>
        </>
      ) : (
        <>
          <p className="login__hint">Zadejte e-mail účtu, pošleme vám jednorázový odkaz pro nastavení nového hesla.</p>
          <label className="login__field">
            <span>Email</span>
            <input type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} disabled={busy} />
          </label>
          {error && <p className="login__error" role="alert">{error}</p>}
          <button className="login__submit" type="submit" disabled={busy || !email.trim()}>
            {busy ? 'Odesílám…' : 'Poslat odkaz'}
          </button>
          <div className="login__links">
            <button type="button" className="login__link" onClick={onBack}>Zpět na přihlášení</button>
          </div>
        </>
      )}
    </form>
  );
}

export function ResetPasswordScreen() {
  const token = new URLSearchParams(window.location.search).get('t') ?? '';
  const [state, setState] = useState<'checking' | 'invalid' | 'form' | 'done'>('checking');
  const [mfaRequired, setMfaRequired] = useState(false);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setState('invalid'); return; }
    validatePasswordReset(token)
      .then((r) => { setMfaRequired(r.mfaRequired); setState(r.valid ? 'form' : 'invalid'); })
      .catch(() => setState('invalid'));
  }, [token]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < 12) { setError('Heslo musí mít alespoň 12 znaků.'); return; }
    if (password !== confirm) { setError('Hesla se neshodují.'); return; }
    if (mfaRequired && !code.trim()) { setError('Zadejte kód z ověřovací aplikace nebo záložní kód.'); return; }
    setBusy(true);
    try {
      await confirmPasswordReset(token, password, mfaRequired ? code.trim() : undefined);
      setState('done');
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'RESET_INVALID') setState('invalid');
        else if (err.code === 'MFA_INVALID_CODE') setError('Kód není platný.');
        else if (err.code === 'MFA_REQUIRED') { setMfaRequired(true); setError('Zadejte kód z ověřovací aplikace.'); }
        else if (err.code === 'RATE_LIMITED') setError('Příliš mnoho pokusů. Zkuste to později.');
        else setError(err.message || 'Heslo nesplňuje pravidla.');
      } else {
        setError('Nastavení hesla selhalo.');
      }
    } finally {
      setBusy(false);
    }
  }

  const toLogin = () => { window.location.href = '/'; };

  if (state === 'checking') {
    return <div className="login__card"><img className="login__logo" src="/assets/platform1-logo.png" alt="Platform 1" /><p className="login__hint">Ověřuji odkaz…</p></div>;
  }
  if (state === 'invalid') {
    return (
      <div className="login__card">
        <img className="login__logo" src="/assets/platform1-logo.png" alt="Platform 1" />
        <h2 className="login__title">Odkaz není platný</h2>
        <p className="login__hint">Odkaz vypršel nebo už byl použit. Požádejte o nový z přihlašovací stránky.</p>
        <button className="login__submit" type="button" onClick={toLogin}>Zpět na přihlášení</button>
      </div>
    );
  }
  if (state === 'done') {
    return (
      <div className="login__card">
        <img className="login__logo" src="/assets/platform1-logo.png" alt="Platform 1" />
        <h2 className="login__title">Heslo je nastavené</h2>
        <p className="login__hint">Všechna předchozí přihlášení byla odhlášena. Přihlaste se novým heslem.</p>
        <button className="login__submit" type="button" onClick={toLogin}>Přihlásit se</button>
      </div>
    );
  }
  return (
    <form className="login__card" onSubmit={onSubmit}>
      <img className="login__logo" src="/assets/platform1-logo.png" alt="Platform 1" />
      <h2 className="login__title">Nové heslo</h2>
      <p className="login__hint">Alespoň 12 znaků, ne z uniklých databází a bez části vašeho e-mailu.</p>
      <label className="login__field">
        <span>Nové heslo</span>
        <input type="password" autoComplete="new-password" required minLength={12} value={password} onChange={(e) => setPassword(e.target.value)} disabled={busy} />
      </label>
      <label className="login__field">
        <span>Potvrzení hesla</span>
        <input type="password" autoComplete="new-password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} disabled={busy} />
      </label>
      {mfaRequired && (
        <label className="login__field">
          <span>Kód z ověřovací aplikace (nebo záložní kód)</span>
          <input type="text" inputMode="numeric" autoComplete="one-time-code" required maxLength={11} value={code} onChange={(e) => setCode(e.target.value)} disabled={busy} />
        </label>
      )}
      {error && <p className="login__error" role="alert">{error}</p>}
      <button className="login__submit" type="submit" disabled={busy}>{busy ? 'Ukládám…' : 'Nastavit heslo'}</button>
      <div className="login__links">
        <button type="button" className="login__link" onClick={toLogin}>Zpět na přihlášení</button>
      </div>
    </form>
  );
}
